import {ActivityCommonProperties} from "../../types-enums/activity-common-properties";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray, TRAVEL_OPTION_TYPE} from "../../types-enums/activity";
import {IconProps} from "atmosfair-ui";
import {VEHICLE_TYPE} from "../../api/model/activity-car-bus";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps
} from "../check/type-guards";
import {activityHasReturnOption} from "../check/activities";

/**
 * Retrieves the person count associated with a given activity.
 *
 * This function calculates the number of people involved in a specific activity
 * based on the type of activity provided. Supported activity types include flights, hotels,
 * and car or bus activities. The function uses type guards to ensure that the correct properties
 * corresponding to the activity type are accessed.
 *
 * @param {AllActivityTypes} item - The activity object containing details about the
 *                                   activity type and associated data.
 * @returns {number | null} The number of people involved in the activity if the activity
 *                               type and state are valid and compatible. Returns `undefined` if
 *                               no matching activity type is found or if the required information is absent.
 */
export const getActivityPersonCount = (item: AllActivityTypes): number | null => {
    const {common: {activityType}} = item
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            if (isActivityFlightState(item)) return item.passengerCount
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(item)) return item.guestCount
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(item)) return item.passengerCount
            break;
        default:
            return activityType satisfies never
    }
    return null
}

/**
 * Filters activities by the specified day.
 *
 * @param {AllActivityTypesArray} activities - An array of activities to filter.
 * @param {ActivityCommonProperties["common"]["day"]} day - The day to filter activities by.
 * @returns {AllActivityTypesArray} A filtered array of activities that match the specified day.
 */
export const getActivitiesByDay = (
    activities: AllActivityTypesArray,
    day: ActivityCommonProperties["common"]["day"],
): AllActivityTypesArray => activities.filter(activity => activity.common.day === day)

/**
 * Filters and retrieves activities based on the specified travel option type.
 *
 * @param {AllActivityTypesArray} activities - An array of activities of various types.
 * @param {TRAVEL_OPTION_TYPE} travelOptionType - The travel option type used to filter activities.
 * @returns {AllActivityTypesArray} A filtered array of activities that match the specified travel option type.
 */
export const getActivitiesByTravelOptionType = (
    activities: AllActivityTypesArray,
    travelOptionType: TRAVEL_OPTION_TYPE
): AllActivityTypesArray => activities.filter(activity => activity.common.travelOptionType === travelOptionType)

/**
 * Finds the index of the first activity in the activities array that matches the given id.
 *
 * @param {AllActivityTypesArray} activities - Array of activity objects containing a common property with an id.
 * @param {string} id - The unique identifier of the activity to locate.
 * @returns {number} The index of the first activity with the matching id, or -1 if not found.
 */
export const getFirstActivityIndexById = (activities: AllActivityTypesArray, id: string): number => activities.map(activity => activity.common.id).indexOf(id)

/**
 * Retrieves the first activity object from the provided activities array
 * that matches the given activityId.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects.
 * @param {string} activityId - The ID of the activity to locate within the array.
 * @returns {AllActivityTypes|null} The first activity object that matches the given activityId,
 *                                  or null if no match is found.
 */
export const getFirstActivityByActivityId = (
    activities: AllActivityTypesArray,
    activityId: string
): AllActivityTypes | null => {
    const index = getFirstActivityIndexById(activities, activityId)
    if (index > -1) return activities[index]
    return null
}

/**
 * Filters and retrieves activities based on a specified activity group ID.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects to be filtered.
 * @param {string | null} groupId - The identifier of the activity group. If null, no filtering occurs, and null is returned.
 * @returns {AllActivityTypesArray | null} Returns an array of activities that match the given groupId. Returns null if groupId is null.
 */
export const getActivitiesByActivityGroupId = (
    activities: AllActivityTypesArray,
    groupId: string | null
): AllActivityTypesArray | null => {
    if (groupId === null) return null
    return activities.filter(activity => activity.common.groupId === groupId)
}

/**
 * Retrieves the first activity from a given group of activities based on the specified group ID.
 *
 * The function filters activities by the specified group ID and returns the first activity
 * if available. If there are no activities matching the given group ID or if the group is empty,
 * it returns null.
 *
 * @param {AllActivityTypesArray} activities - An array of all activities to be filtered.
 * @param {string} groupId - The identifier of the activity group used for filtering.
 * @returns {AllActivityTypes | null} The first activity matching the specified group ID, or null if no matching activity is found.
 */
export const getFirstActivityByActivityGroupId = (
    activities: AllActivityTypesArray,
    groupId: string
): AllActivityTypes | null => {
    const allActivities = getActivitiesByActivityGroupId(activities, groupId)
    if (allActivities !== null && allActivities?.length > 0) return allActivities[0]
    return null
}

/**
 * Retrieves the activity that corresponds to the return ID of the given activity, if applicable.
 *
 * @param {AllActivityTypes} activity - The activity object to check for a return option.
 * @param {AllActivityTypesArray} activities - An array of activity objects to search within.
 * @returns {AllActivityTypes | null} The activity corresponding to the return ID, or null if no such activity exists
 *                                    or the given activity does not have a return option.
 */
export const getReturnActivity = (activity: AllActivityTypes, activities: AllActivityTypesArray): AllActivityTypes | null => {
    if (activityHasReturnOption(activity)) {
        const {returnId} = activity
        if (returnId === null || typeof returnId === "undefined") return null
        const foundActivity = activities.find(activity => {
            if (isActivityWithReturnRouteProps(activity) && activity.common.id === returnId) return true
            return false
        })
        return foundActivity || null
    }
    return null
}

/**
 * Retrieves a one-way activity from a list of activities that matches the return route ID of the provided activity.
 *
 * @param {AllActivityTypes} oneWayActivity - The activity object containing details about the one-way activity.
 * @param {AllActivityTypesArray} activities - An array of activity objects to search through.
 * @returns {AllActivityTypes|null} The matching activity object if found, otherwise returns null.
 */
export const getOneWayActivity = (oneWayActivity: AllActivityTypes, activities: AllActivityTypesArray) => {
    const {common: {id: oneWayActivityId}} = oneWayActivity
    const foundActivity = activities.find(activity => {
        if (isActivityWithReturnRouteProps(activity) && activity.returnId === oneWayActivityId) return true
        return false
    })
    return foundActivity || null
}

/**
 * Retrieves the return and one-way activity associated with a given activity.
 * Determines whether the provided activity has a return option and identifies
 * both the return activity and the one-way activity based on the activity properties.
 *
 * @param {AllActivityTypes} activity - The activity for which return and one-way activities are to be determined.
 * @param {AllActivityTypesArray} activities - The array of all activity types to search within.
 * @returns {{returnActivity: AllActivityTypes | null, oneWayActivity: AllActivityTypes | null}}
 *          An object containing the identified return activity and one-way activity. Both fields will be null if no matches are found.
 */
export const getReturnAndOneWayActivity = (activity: AllActivityTypes, activities: AllActivityTypesArray): {
    returnActivity: AllActivityTypes | null;
    oneWayActivity: AllActivityTypes | null;
} => {
    let returnActivity: AllActivityTypes | null = null
    let oneWayActivity: AllActivityTypes | null = null
    if (activityHasReturnOption(activity)) {
        if (activity.returnId !== null) {
            returnActivity = getReturnActivity(activity, activities)
            oneWayActivity = activity
        } else {
            returnActivity = activity
            oneWayActivity = getOneWayActivity(activity, activities)
        }
    }
    return {returnActivity, oneWayActivity}
}


type ActivityIconMappingProps = {
    [key in ACTIVITY_TYPE | VEHICLE_TYPE]: IconProps["icon"]
}

export const ACTIVITY_ICON_MAPPING: ActivityIconMappingProps = {
    [ACTIVITY_TYPE.flight]: "planeDeparture",
    [ACTIVITY_TYPE.car_bus]: "carSide",
    [ACTIVITY_TYPE.hotel]: "hotel",
    [VEHICLE_TYPE.car]: "carSide",
    [VEHICLE_TYPE.bus]: "busSimple",
}